import type { Alpine as AlpineAPI } from 'alpinejs';

/** Adds a panel-wide drop target while keeping native multipart form submission. */
export function registerFileUpload(Alpine: AlpineAPI) {
    Alpine.data('evidenceUpload', (maxBytes: number) => ({
        file: null as File | null,
        error: '',
        dragDepth: 0,
        get dragging() { return this.dragDepth > 0; },
        get fileSize() {
            if (!this.file) return '';
            return this.file.size < 1048576
                ? `${Math.max(1, Math.ceil(this.file.size / 1024))} KB`
                : `${(this.file.size / 1048576).toFixed(1)} MB`;
        },
        dragEnter(event: DragEvent) {
            if (!event.dataTransfer?.types.includes('Files')) return;
            event.preventDefault();
            this.dragDepth++;
        },
        dragOver(event: DragEvent) {
            if (!event.dataTransfer?.types.includes('Files')) return;
            event.preventDefault();
            event.dataTransfer.dropEffect = 'copy';
        },
        dragLeave(event: DragEvent) {
            if (!event.dataTransfer?.types.includes('Files')) return;
            this.dragDepth = Math.max(0, this.dragDepth - 1);
        },
        drop(event: DragEvent) {
            if (!event.dataTransfer?.types.includes('Files')) return;
            event.preventDefault();
            event.stopPropagation();
            this.dragDepth = 0;
            this.selectFiles(event.dataTransfer.files);
        },
        selectFiles(files: FileList | null) {
            const input = this.$refs.fileInput as HTMLInputElement;
            const file = files?.[0];
            this.error = '';
            if (!file) this.error = 'Choose a file from your device.';
            else if (files!.length !== 1) this.error = 'Choose one signed diary page file at a time.';
            else if (!/\.(pdf|jpe?g|png|gif|webp|hei[cf])$/i.test(file.name)) this.error = 'Choose a PDF, JPG, PNG, GIF, WebP or HEIC file.';
            else if (!file.size) this.error = 'That file is empty. Choose another file.';
            else if (file.size > maxBytes) this.error = `That file is too large. The maximum size is ${maxBytes / 1048576} MB.`;

            if (this.error || !file) {
                this.file = null;
                input.value = '';
                input.setCustomValidity(this.error);
                return;
            }
            // A dropped file must also populate the real input used by PHP's $_FILES.
            const transfer = new DataTransfer();
            transfer.items.add(file);
            input.files = transfer.files;
            input.setCustomValidity('');
            this.file = file;
        },
        removeFile() {
            const input = this.$refs.fileInput as HTMLInputElement;
            input.value = '';
            input.setCustomValidity('');
            this.file = null;
            this.error = '';
            this.$refs.chooseFile.focus();
        },
        showRequiredError() {
            if (!this.error) this.error = 'Attach your signed diary page.';
            this.$refs.chooseFile.focus();
        },
    }));
}
